import type {
  TrainingRolloutProgress,
  TrainingRunDocument,
  TrainingStepRollouts,
  TrainingValidationPoint,
} from '@/api/generated/model'

export interface RolloutRow {
  step: number
  trainable: number
  /** Finished rollouts in groups the trainer cannot learn from. */
  untrainable: number
  cancelled: number
}

/** One stacked bar per step, oldest first: trainable under untrainable. */
export function rolloutRows(progress: TrainingRolloutProgress): RolloutRow[] {
  return progress.steps.map((step) => ({
    step: step.step,
    trainable: step.trainable,
    untrainable: step.done - step.trainable,
    cancelled: step.cancelled,
  }))
}

/**
 * The step the trainer is generating for: the newest one, while the run
 * runs and its optimizer step has not completed. Null otherwise.
 */
export function liveStep(
  run: TrainingRunDocument,
  progress: TrainingRolloutProgress,
): TrainingStepRollouts | null {
  const newest = progress.steps[progress.steps.length - 1]
  if (newest === undefined || run.status !== 'running' || newest.step <= run.current_step) {
    return null
  }
  return newest
}

export interface ValidationRow {
  step: number
  reward: number
  datasets: Record<string, number>
}

/** One chart row per validation, with each dataset's mean by name. */
export function validationRows(points: TrainingValidationPoint[]): ValidationRow[] {
  return points.map((point) => ({
    step: point.step,
    reward: point.reward,
    datasets: Object.fromEntries(point.datasets.map((item) => [item.name, item.reward])),
  }))
}

/** Every dataset any validation scored, by name. */
export function validationDatasets(points: TrainingValidationPoint[]): string[] {
  return [...new Set(points.flatMap((point) => point.datasets.map((item) => item.name)))].sort()
}

export interface ValidationSummary {
  name: string
  samples: number
  first: number
  latest: number
}

/** Each dataset's mean at its first and latest validation, overall first. */
export function validationSummary(points: TrainingValidationPoint[]): ValidationSummary[] {
  const first = points[0]
  const latest = points[points.length - 1]
  if (first === undefined || latest === undefined) return []
  const rows = [
    { name: 'All datasets', samples: latest.samples, first: first.reward, latest: latest.reward },
  ]
  for (const name of validationDatasets(points)) {
    const scored = points.flatMap((point) => point.datasets.filter((item) => item.name === name))
    const start = scored[0]
    const end = scored[scored.length - 1]
    if (start === undefined || end === undefined) throw new Error(`No validation scored ${name}`)
    rows.push({ name, samples: end.samples, first: start.reward, latest: end.reward })
  }
  return rows
}

export interface ValidationTaskSummary {
  task: string
  samples: number
  first: number
  latest: number
}

/**
 * Each task of one dataset from its first to its latest validation, by
 * name. Empty for a run whose node counted no tasks (documents written
 * before the node did carry null), so the page shows the dataset alone.
 */
export function validationTasks(points: TrainingValidationPoint[], name: string): ValidationTaskSummary[] {
  const rows = new Map<string, ValidationTaskSummary>()
  for (const point of points) {
    for (const dataset of point.datasets) {
      const tasks = dataset.tasks
      if (dataset.name !== name || tasks === null || tasks === undefined) continue
      for (const task of tasks) {
        const seen = rows.get(task.task)
        rows.set(task.task, {
          task: task.task,
          samples: task.samples,
          first: seen === undefined ? task.reward : seen.first,
          latest: task.reward,
        })
      }
    }
  }
  return [...rows.values()].sort((a, b) => a.task.localeCompare(b.task))
}

/**
 * The reward axis: zero to one, widened to every reward plotted, since a
 * dataset's reward contract may score outside it.
 */
export function rewardDomain(points: TrainingValidationPoint[]): [number, number] {
  const rewards = points.flatMap((point) => [point.reward, ...point.datasets.map((item) => item.reward)])
  return [Math.min(0, ...rewards), Math.max(1, ...rewards)]
}

/**
 * Whole-step ticks from 0 to the run's last step, at most six of them, at a
 * round interval (1, 2 or 5 times a power of ten). The regular tick before
 * the last step is dropped when it would crowd it.
 */
export function stepTicks(totalSteps: number): number[] {
  const every = roundInterval(totalSteps / 5)
  const ticks: number[] = []
  for (let step = 0; step < totalSteps; step += every) ticks.push(step)
  const before = ticks[ticks.length - 1]
  if (ticks.length > 1 && before !== undefined && totalSteps - before <= every / 2) ticks.pop()
  return [...ticks, totalSteps]
}

function roundInterval(least: number): number {
  if (!Number.isFinite(least)) throw new Error(`A step count must be a number, not ${String(least)}`)
  for (let power = 1; ; power *= 10) {
    for (const step of [1, 2, 5]) if (step * power >= least) return step * power
  }
}
